import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { notify } from "../lib/pwa";
import Seo from "../components/Seo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const session_id = params.get("session_id");
  const booking_id = params.get("booking_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!session_id) { setStatus("failed"); return; }
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          notify({ title: "Booking confirmed ✓", body: "Your DriveTerrain session is paid. Generate your OTP to start.", url: `/session/${booking_id}`, tag: `paid-${booking_id}` });
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
      } catch (e) {
        // Transient failures are expected while the webhook settles, so we keep
        // polling — but a persistent error should not vanish silently.
        console.warn(`Payment status poll ${attempts.current + 1}/8 failed:`, e);
      }
      attempts.current += 1;
      if (attempts.current >= 8) {
        console.error(`Payment status for session ${session_id} unresolved after 8 attempts`);
        setStatus("failed");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [session_id, booking_id]);

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16 text-center" data-testid="payment-status-page">
      <Seo title="Payment Complete – DriveTerrain" description="Your booking is confirmed." canonical="/payment-success" robots="noindex, nofollow" hrefLangs={false} />
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
          <h1 className="font-display text-2xl font-semibold mt-6">Confirming your payment</h1>
          <p className="text-muted-foreground text-sm mt-2">Please don't close this tab.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-primary" data-testid="payment-success-icon" />
          <h1 className="font-display text-3xl font-semibold mt-6">Payment successful</h1>
          <p className="text-muted-foreground mt-2">Your session is booked. Ask the trainer to start the lesson with your OTP.</p>
          <div className="flex gap-2 justify-center mt-8">
            <Link to={`/session/${booking_id}`}><Button className="rounded-sm" data-testid="view-otp-btn">Get session OTP</Button></Link>
            <Link to="/learner"><Button variant="outline" className="rounded-sm">Dashboard</Button></Link>
          </div>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-destructive" />
          <h1 className="font-display text-3xl font-semibold mt-6">Payment not confirmed</h1>
          <p className="text-muted-foreground mt-2">If you were charged, refresh in a minute or contact support.</p>
          <Button className="rounded-sm mt-6" onClick={() => nav("/learner")} data-testid="back-dashboard">Back to dashboard</Button>
        </>
      )}
    </div>
  );
}
